import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import {
  Plus,
  Trash2,
  Edit3,
  Check,
  X,
  Download,
  LogOut,
  FileText,
  Heart,
  Inbox,
  Eye,
  EyeOff,
  ExternalLink,
  ImagePlus,
  Loader2,
  Images,
  Video,
  ArrowUp,
  ArrowDown,
  Youtube,
  Link2,
  Upload,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { RichTextEditor } from "@/components/RichTextEditor";

const TABS = [
  { id: "posts", label: "Artigos", icon: FileText },
  { id: "hero", label: "Topo do site", icon: Images },
  { id: "videos", label: "Vídeos", icon: Video },
  { id: "testimonials", label: "Depoimentos", icon: Heart },
  { id: "contacts", label: "Mensagens", icon: Inbox },
];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function resolveImageUrl(url) {
  if (!url) return "";
  return url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
}

// ---------- COVER IMAGE UPLOAD ----------
function CoverImageField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem (JPG, PNG, GIF ou WEBP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Imagem muito grande. Tamanho máximo: 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
      toast.success("Imagem enviada!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar a imagem.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div data-testid="admin-post-cover-field" className="space-y-3">
      <p className="text-sm text-[#5B685C]">Imagem de capa (opcional)</p>
      <input
        ref={inputRef}
        data-testid="admin-post-cover-file"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFile}
        className="hidden"
      />
      {value ? (
        <div className="flex items-start gap-4">
          <img
            src={resolveImageUrl(value)}
            alt="Capa do artigo"
            data-testid="admin-post-cover-preview"
            className="w-40 h-24 object-cover rounded-xl border border-[#8BA88E]/30"
          />
          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid="admin-post-cover-change"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="btn-outline !py-2 !px-3 text-xs"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
              {uploading ? "Enviando..." : "Trocar imagem"}
            </button>
            <button
              type="button"
              data-testid="admin-post-cover-remove"
              onClick={() => onChange("")}
              className="px-3 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs inline-flex items-center gap-1 justify-center"
            >
              <X size={14} /> Remover
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          data-testid="admin-post-cover-upload"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-full rounded-xl border-2 border-dashed border-[#8BA88E]/40 bg-[#FAF9F6] px-4 py-6 text-[#5B685C] hover:border-[#8BA88E] hover:bg-[#F5F5ED] transition-colors flex flex-col items-center gap-2 text-sm font-light"
        >
          {uploading ? <Loader2 size={22} className="animate-spin text-[#8BA88E]" /> : <ImagePlus size={22} className="text-[#8BA88E]" />}
          {uploading ? "Enviando imagem..." : "Clique para enviar uma imagem do seu computador"}
          <span className="text-xs text-[#5B685C]/70">JPG, PNG, GIF ou WEBP · máx. 8 MB</span>
        </button>
      )}
    </div>
  );
}

// ---------- POSTS ----------
function PostsPanel() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const empty = { title: "", excerpt: "", content: "", cover_image: "", published: true };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/posts").then((r) => setPosts(r.data));

  useEffect(() => { load(); }, []);

  const startEdit = (p) => {
    setEditing(p.id);
    setForm({ title: p.title, excerpt: p.excerpt, content: p.content, cover_image: p.cover_image || "", published: p.published });
    setShowForm(true);
  };

  const startNew = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  };

  const cancel = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(false);
  };

  const save = async () => {
    const cleanedContent = form.content.replace(/&nbsp;|\u00a0/g, " ");
    const plainContent = cleanedContent.replace(/<[^>]+>/g, "").trim();
    if (!form.title.trim() || !form.excerpt.trim() || !plainContent) {
      toast.error("Preencha o título, o resumo e o conteúdo antes de salvar.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, content: cleanedContent, cover_image: form.cover_image || null };
      if (editing) {
        await api.put(`/admin/posts/${editing}`, payload);
        toast.success("Artigo atualizado.");
      } else {
        await api.post(`/admin/posts`, payload);
        toast.success("Artigo publicado.");
      }
      await load();
      cancel();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este artigo?")) return;
    await api.delete(`/admin/posts/${id}`);
    toast.success("Artigo excluído.");
    load();
  };

  return (
    <div data-testid="admin-posts-panel">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-3xl text-[#2C352D]">Artigos</h2>
        <button data-testid="admin-new-post-btn" onClick={startNew} className="btn-primary">
          <Plus size={16} /> Novo artigo
        </button>
      </div>

      {showForm && (
        <div data-testid="admin-post-form" className="bg-white rounded-2xl p-6 md:p-8 mb-8 soft-shadow border border-[#8BA88E]/15">
          <h3 className="font-serif text-xl text-[#2C352D] mb-4">
            {editing ? "Editar artigo" : "Novo artigo"}
          </h3>
          <div className="space-y-4">
            <input
              data-testid="admin-post-title"
              placeholder="Título"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] font-light"
            />
            <CoverImageField
              value={form.cover_image}
              onChange={(url) => setForm((f) => ({ ...f, cover_image: url }))}
            />
            <textarea
              data-testid="admin-post-excerpt"
              rows={2}
              placeholder="Resumo (1-2 linhas)"
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              className="w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] font-light resize-none"
            />
            <RichTextEditor
              value={form.content}
              onChange={(html) => setForm((f) => ({ ...f, content: html }))}
              placeholder="Escreva o conteúdo do artigo aqui. Use a barra de ferramentas para formatar o texto e inserir imagens."
            />
            <label className="flex items-center gap-2 text-sm text-[#5B685C]">
              <input
                data-testid="admin-post-published"
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
              />
              Publicado
            </label>
          </div>
          <div className="flex gap-3 mt-6">
            <button data-testid="admin-post-save" onClick={save} disabled={saving} className="btn-primary">
              {saving ? "Salvando..." : "Salvar"}
            </button>
            <button data-testid="admin-post-cancel" onClick={cancel} className="btn-outline">
              Cancelar
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl soft-shadow border border-[#8BA88E]/15 overflow-hidden">
        {posts.length === 0 && (
          <p className="p-6 text-[#5B685C] font-light">Nenhum artigo cadastrado.</p>
        )}
        {posts.map((p) => (
          <div key={p.id} data-testid={`admin-post-row-${p.id}`} className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 border-b border-[#8BA88E]/10 last:border-b-0">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-serif text-lg text-[#2C352D] truncate">{p.title}</h4>
                {!p.published && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Rascunho</span>
                )}
              </div>
              <p className="text-sm text-[#5B685C] font-light truncate">{p.excerpt}</p>
              <p className="text-xs text-[#5B685C] mt-1">{formatDate(p.created_at)} · /{p.slug}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <Link to={`/blog/${p.slug}`} target="_blank" className="btn-outline !py-2 !px-3 text-xs">
                <ExternalLink size={14} /> Ver
              </Link>
              <button data-testid={`admin-post-edit-${p.id}`} onClick={() => startEdit(p)} className="btn-outline !py-2 !px-3 text-xs">
                <Edit3 size={14} /> Editar
              </button>
              <button data-testid={`admin-post-delete-${p.id}`} onClick={() => remove(p.id)} className="px-3 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs inline-flex items-center gap-1">
                <Trash2 size={14} /> Excluir
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- HERO / BANNER DO TOPO ----------
function HeroPanel() {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const load = () => api.get("/hero-images").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem (JPG, PNG, GIF ou WEBP).");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Imagem muito grande. Tamanho máximo: 8 MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/hero-images", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Imagem adicionada ao topo do site!");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar a imagem.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta imagem do topo do site?")) return;
    await api.delete(`/admin/hero-images/${id}`);
    toast.success("Imagem removida.");
    load();
  };

  return (
    <div data-testid="admin-hero-panel">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h2 className="font-serif text-3xl text-[#2C352D]">Imagens do topo do site</h2>
        <button
          data-testid="admin-hero-add-btn"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="btn-primary"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />}
          {uploading ? "Enviando..." : "Adicionar imagem"}
        </button>
      </div>
      <p className="text-sm text-[#5B685C] font-light mb-8">
        Com mais de uma imagem, o topo do site exibe uma transição automática de slides (a cada 10 segundos).
        Recomendado: imagens largas (ex.: 1920×600).
      </p>
      <input
        ref={inputRef}
        data-testid="admin-hero-file"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFile}
        className="hidden"
      />
      <div className="grid sm:grid-cols-2 gap-5">
        {items.length === 0 && (
          <p className="text-[#5B685C] font-light col-span-2">
            Nenhuma imagem cadastrada. O site exibirá o banner padrão.
          </p>
        )}
        {items.map((img, i) => (
          <div key={img.id} data-testid={`admin-hero-item-${img.id}`} className="bg-white rounded-2xl overflow-hidden border border-[#8BA88E]/15 soft-shadow">
            <img
              src={resolveImageUrl(img.url)}
              alt={`Imagem do topo ${i + 1}`}
              className="w-full h-40 object-cover"
            />
            <div className="flex items-center justify-between p-4">
              <span className="text-xs text-[#5B685C]">Slide {i + 1}</span>
              <button
                data-testid={`admin-hero-delete-${img.id}`}
                onClick={() => remove(img.id)}
                className="px-3 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs inline-flex items-center gap-1"
              >
                <Trash2 size={14} /> Remover
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- VÍDEOS (Sobre Mim) ----------
const CHUNK_SIZE = 4 * 1024 * 1024;

function VideosPanel() {
  const [items, setItems] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [progress, setProgress] = useState(null);
  const [addingLink, setAddingLink] = useState(false);
  const fileRef = React.useRef(null);

  const load = () => api.get("/videos").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const uploadVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["video/mp4", "video/webm", "video/quicktime"].includes(file.type)) {
      toast.error("Formato inválido. Envie um vídeo MP4, WEBM ou MOV.");
      return;
    }
    if (file.size > 150 * 1024 * 1024) {
      toast.error("Vídeo muito grande. Tamanho máximo: 150 MB.");
      return;
    }
    const uploadId = crypto.randomUUID();
    const total = Math.ceil(file.size / CHUNK_SIZE);
    setProgress(0);
    try {
      for (let i = 0; i < total; i++) {
        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", i);
        fd.append("file", chunk, "chunk");
        await api.post("/admin/videos/upload/chunk", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setProgress(Math.round(((i + 1) / total) * 90));
      }
      await api.post("/admin/videos/upload/finish", {
        upload_id: uploadId,
        filename: file.name,
        content_type: file.type,
      });
      setProgress(100);
      toast.success("Vídeo publicado!");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar o vídeo.");
    } finally {
      setTimeout(() => setProgress(null), 800);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addLink = async () => {
    if (!linkUrl.trim()) return;
    setAddingLink(true);
    try {
      await api.post("/admin/videos/link", { url: linkUrl.trim() });
      toast.success("Vídeo adicionado!");
      setLinkUrl("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao adicionar o link.");
    } finally {
      setAddingLink(false);
    }
  };

  const move = async (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    await api.put("/admin/videos/reorder", { ids: next.map((v) => v.id) });
    toast.success("Ordem atualizada.");
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este vídeo do site?")) return;
    await api.delete(`/admin/videos/${id}`);
    toast.success("Vídeo removido.");
    load();
  };

  const typeLabel = { upload: "Arquivo enviado", youtube: "YouTube", instagram: "Instagram", facebook: "Facebook" };

  return (
    <div data-testid="admin-videos-panel">
      <h2 className="font-serif text-3xl text-[#2C352D] mb-2">Vídeos — Sobre Mim</h2>
      <p className="text-sm text-[#5B685C] font-light mb-8">
        Os vídeos aparecem em um carrossel abaixo da sua foto na página "Sobre Mim". Use as setas para definir a ordem.
      </p>

      <div className="grid md:grid-cols-2 gap-5 mb-10">
        <div className="bg-white rounded-2xl p-6 border border-[#8BA88E]/15 soft-shadow">
          <p className="font-medium text-[#2C352D] mb-3 flex items-center gap-2"><Upload size={16} /> Enviar vídeo do computador</p>
          <input
            ref={fileRef}
            data-testid="admin-video-file"
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            onChange={uploadVideo}
            className="hidden"
          />
          <button
            data-testid="admin-video-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={progress !== null}
            className="btn-primary w-full justify-center"
          >
            {progress !== null ? <Loader2 size={16} className="animate-spin" /> : <Video size={16} />}
            {progress !== null ? `Enviando... ${progress}%` : "Escolher vídeo (MP4)"}
          </button>
          {progress !== null && (
            <div className="mt-3 h-2 rounded-full bg-[#F5F5ED] overflow-hidden">
              <div className="h-full bg-[#8BA88E] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
          <p className="text-xs text-[#5B685C]/70 mt-3">MP4, WEBM ou MOV · máx. 150 MB · vídeos verticais ficam melhores</p>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-[#8BA88E]/15 soft-shadow">
          <p className="font-medium text-[#2C352D] mb-3 flex items-center gap-2"><Link2 size={16} /> Adicionar por link</p>
          <input
            data-testid="admin-video-link-input"
            placeholder="Cole o link do YouTube, Instagram ou Facebook"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            className="w-full rounded-xl border border-[#8BA88E]/30 bg-[#FAF9F6] px-4 py-3 outline-none focus:border-[#8BA88E] font-light text-sm"
          />
          <button
            data-testid="admin-video-link-btn"
            onClick={addLink}
            disabled={addingLink || !linkUrl.trim()}
            className="btn-outline w-full justify-center mt-3"
          >
            {addingLink ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Adicionar vídeo
          </button>
          <p className="text-xs text-[#5B685C]/70 mt-3">Ex.: YouTube Shorts, Reels do Instagram ou vídeo do Facebook</p>
        </div>
      </div>

      <div className="space-y-4">
        {items.length === 0 && (
          <p className="text-[#5B685C] font-light">Nenhum vídeo publicado ainda.</p>
        )}
        {items.map((v, i) => (
          <div
            key={v.id}
            data-testid={`admin-video-item-${v.id}`}
            className="bg-white rounded-2xl p-4 border border-[#8BA88E]/15 soft-shadow flex items-center gap-4"
          >
            <div className="w-16 h-24 rounded-xl overflow-hidden bg-black shrink-0">
              {v.type === "upload" ? (
                <video src={resolveImageUrl(v.url)} muted preload="metadata" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/80">
                  {v.type === "youtube" ? <Youtube size={22} /> : <Video size={22} />}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-[#2C352D] text-sm">Vídeo {i + 1} · {typeLabel[v.type] || v.type}</p>
              {v.source_url && (
                <p className="text-xs text-[#5B685C] truncate">{v.source_url}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                data-testid={`admin-video-up-${v.id}`}
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="p-2 rounded-full border border-[#8BA88E]/30 text-[#2C352D] disabled:opacity-30 hover:bg-[#F5F5ED]"
                aria-label="Mover para cima"
              >
                <ArrowUp size={15} />
              </button>
              <button
                data-testid={`admin-video-down-${v.id}`}
                onClick={() => move(i, 1)}
                disabled={i === items.length - 1}
                className="p-2 rounded-full border border-[#8BA88E]/30 text-[#2C352D] disabled:opacity-30 hover:bg-[#F5F5ED]"
                aria-label="Mover para baixo"
              >
                <ArrowDown size={15} />
              </button>
              <button
                data-testid={`admin-video-delete-${v.id}`}
                onClick={() => remove(v.id)}
                className="p-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50"
                aria-label="Remover vídeo"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- TESTIMONIALS ----------
function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const setApproved = async (id, approved) => {
    await api.put(`/admin/testimonials/${id}`, null, { params: { approved } });
    toast.success(approved ? "Depoimento publicado." : "Depoimento ocultado.");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este depoimento?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Depoimento excluído.");
    load();
  };

  return (
    <div data-testid="admin-testimonials-panel">
      <h2 className="font-serif text-3xl text-[#2C352D] mb-6">Depoimentos</h2>
      <div className="grid md:grid-cols-2 gap-5">
        {items.length === 0 && (
          <p className="text-[#5B685C] font-light col-span-2">Nenhum depoimento recebido.</p>
        )}
        {items.map((t) => (
          <div key={t.id} data-testid={`admin-testimonial-${t.id}`} className="bg-white rounded-2xl p-6 border border-[#8BA88E]/15 soft-shadow">
            <div className="flex items-start justify-between gap-3">
              <p className="font-serif text-lg text-[#2C352D]">{t.name}</p>
              {t.approved ? (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Publicado</span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Pendente</span>
              )}
            </div>
            <p className="text-[#5B685C] font-light italic mt-2">“{t.message}”</p>
            <p className="text-xs text-[#5B685C] mt-3">{formatDate(t.created_at)}</p>
            <div className="flex gap-2 mt-4">
              {!t.approved ? (
                <button data-testid={`admin-testimonial-approve-${t.id}`} onClick={() => setApproved(t.id, true)} className="btn-primary !py-2 !px-3 text-xs">
                  <Check size={14} /> Aprovar
                </button>
              ) : (
                <button data-testid={`admin-testimonial-hide-${t.id}`} onClick={() => setApproved(t.id, false)} className="btn-outline !py-2 !px-3 text-xs">
                  <EyeOff size={14} /> Ocultar
                </button>
              )}
              <button data-testid={`admin-testimonial-delete-${t.id}`} onClick={() => remove(t.id)} className="px-3 py-2 rounded-full border border-red-200 text-red-600 hover:bg-red-50 text-xs inline-flex items-center gap-1">
                <Trash2 size={14} /> Excluir
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- CONTACTS ----------
function ContactsPanel() {
  const [items, setItems] = useState([]);
  const [opened, setOpened] = useState({});
  const load = () => api.get("/admin/contacts").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Excluir esta mensagem?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Mensagem excluída.");
    load();
  };

  const toggleRead = async (id, isRead) => {
    if (!isRead) {
      await api.put(`/admin/contacts/${id}/read`);
    }
    setOpened((s) => ({ ...s, [id]: !s[id] }));
    load();
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("admin_token");
    const res = await fetch(`${API}/admin/contacts/export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      toast.error("Erro ao exportar.");
      return;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contatos.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-contacts-panel">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h2 className="font-serif text-3xl text-[#2C352D]">Mensagens recebidas</h2>
        <button data-testid="admin-contacts-export" onClick={exportCsv} className="btn-outline">
          <Download size={16} /> Exportar planilha (CSV)
        </button>
      </div>

      <div className="bg-white rounded-2xl soft-shadow border border-[#8BA88E]/15 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5ED] text-[#5B685C]">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Data</th>
                <th className="text-left px-4 py-3 font-medium">Nome</th>
                <th className="text-left px-4 py-3 font-medium">E-mail</th>
                <th className="text-left px-4 py-3 font-medium">Telefone</th>
                <th className="text-left px-4 py-3 font-medium">Assunto</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-[#5B685C] font-light text-center">
                    Nenhuma mensagem recebida.
                  </td>
                </tr>
              )}
              {items.map((c) => (
                <React.Fragment key={c.id}>
                  <tr data-testid={`admin-contact-${c.id}`} className="border-t border-[#8BA88E]/10">
                    <td className="px-4 py-3 text-[#5B685C]">{formatDate(c.created_at)}</td>
                    <td className="px-4 py-3 text-[#2C352D] font-medium">{c.name}</td>
                    <td className="px-4 py-3 text-[#5B685C] break-all">{c.email}</td>
                    <td className="px-4 py-3 text-[#5B685C]">{c.phone || "—"}</td>
                    <td className="px-4 py-3 text-[#5B685C]">{c.subject || "—"}</td>
                    <td className="px-4 py-3">
                      {c.read ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-[#8BA88E]/15 text-[#5B685C]">Lida</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Nova</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`admin-contact-view-${c.id}`} onClick={() => toggleRead(c.id, c.read)} className="text-[#8BA88E] hover:text-[#769379] mr-3">
                        <Eye size={16} />
                      </button>
                      <button data-testid={`admin-contact-delete-${c.id}`} onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                  {opened[c.id] && (
                    <tr data-testid={`admin-contact-detail-${c.id}`} className="bg-[#FAF9F6]">
                      <td colSpan={7} className="px-4 py-4 text-[#5B685C] font-light whitespace-pre-wrap">
                        {c.message}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------- MAIN ----------
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("posts");

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-[#F5F5ED]">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-[#8BA88E]/15">
        <div className="container-narrow py-5 flex items-center justify-between">
          <div>
            <Link to="/" className="font-serif text-xl text-[#2C352D]">Cristina Moreira</Link>
            <p className="text-eyebrow mt-1">Painel administrativo</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-[#5B685C]">{user?.email}</span>
            <button data-testid="admin-logout-btn" onClick={handleLogout} className="btn-outline !py-2 !px-4 text-xs">
              <LogOut size={14} /> Sair
            </button>
          </div>
        </div>
      </header>

      <div className="container-narrow py-10">
        <div className="flex flex-wrap gap-2 mb-10 bg-white p-2 rounded-2xl border border-[#8BA88E]/15 soft-shadow inline-flex">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`admin-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`px-5 py-2.5 rounded-xl text-sm inline-flex items-center gap-2 transition-all ${
                  tab === t.id
                    ? "bg-[#8BA88E] text-white shadow-md"
                    : "text-[#5B685C] hover:text-[#2C352D]"
                }`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        {tab === "posts" && <PostsPanel />}
        {tab === "hero" && <HeroPanel />}
        {tab === "videos" && <VideosPanel />}
        {tab === "testimonials" && <TestimonialsPanel />}
        {tab === "contacts" && <ContactsPanel />}
      </div>
    </div>
  );
}
